import { FC, useCallback } from 'react'

import { AvatarVariant, FiltersState } from '@factorialco/f0-react'
import {
  BookableEmployeeForSelectFragment,
  BookableEmployeeSelectorDocument,
  BookableEmployeeSelectorItemsDocument,
  BookableEmployeeSelectorItemsQuery,
  BookableEmployeeSelectorQuery,
  BookableEmployeeSelectorQueryVariables,
} from 'generated/resources/graphql'

import { EntitySelectorV2 } from 'modules/core/components/EntitySelectorV2'
import buildUrl from 'modules/core/lib/buildUrl'
import { resolveEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { useLegalEntityFilter } from 'modules/employees/lib/filters/legalEntity'
import { useTeamFilter } from 'modules/employees/lib/filters/team'
import { useWorkplaceFilter } from 'modules/employees/lib/filters/workplace'

type BookableEmployeeSelectorFilters = {
  workplace: ReturnType<typeof useWorkplaceFilter>
  team: ReturnType<typeof useTeamFilter>
  legalEntity: ReturnType<typeof useLegalEntityFilter>
}

const useBookableEmployeeFilters = (): BookableEmployeeSelectorFilters => {
  const workplaceFilter = useWorkplaceFilter()
  const teamFilter = useTeamFilter()
  const legalEntityFilter = useLegalEntityFilter()

  return {
    workplace: workplaceFilter,
    team: teamFilter,
    legalEntity: legalEntityFilter,
  }
}

interface Props {
  /** The accessId of the selected employee */
  value?: number
  /** Called with the accessId when selection changes */
  onChange: (accessId: number | undefined) => void
  label?: string
  disabled?: boolean
}

/**
 * A selector for bookable employees that works with accessId.
 * The backend handles permission filtering - only employees the current user
 * can create/edit bookings for are returned.
 */
const BookableEmployeeSelector: FC<Props> = ({ value, onChange, label, disabled }) => {
  const filters = useBookableEmployeeFilters()

  const extractConnection = useCallback(
    (data: BookableEmployeeSelectorQuery) => data.spaceControl.bookableEmployeesConnection,
    []
  )

  const extractItems = useCallback(
    (data: BookableEmployeeSelectorItemsQuery) =>
      data.spaceControl.bookableEmployeesConnection.nodes,
    []
  )

  const mapItemTitle = useCallback(
    (item: BookableEmployeeForSelectFragment) =>
      resolveEmployeeDisplayName({
        fullName: item.fullName,
        lastName: item.lastName,
        preferredName: item.preferredName,
      }),
    []
  )

  const mapItemAvatar = useCallback((item: BookableEmployeeForSelectFragment): AvatarVariant => {
    return {
      firstName: item.preferredName ?? item.firstName,
      lastName: item.lastName,
      src: buildUrl(item.access?.avatar),
      type: 'person',
    }
  }, [])

  // Use accessId as the value for the selector
  const mapItemValue = useCallback((item: BookableEmployeeForSelectFragment) => item.access.id, [])

  const mapFiltersToVariables = useCallback(
    ({
      filters: filtersState,
    }: {
      filters?: FiltersState<typeof filters>
    }): Omit<BookableEmployeeSelectorQueryVariables, 'cursor' | 'perPage' | 'page'> => {
      let locationIds: number[] | undefined = undefined
      let legalEntityIds: number[] | undefined = undefined
      let teamIds: number[] | undefined = undefined

      if (filtersState?.workplace?.length) {
        locationIds = [...filtersState.workplace]
      }

      if (filtersState?.legalEntity?.length) {
        legalEntityIds = [...filtersState.legalEntity]
      }

      if (filtersState?.team?.length) {
        teamIds = [...filtersState.team]
      }

      return {
        legalEntityIds,
        locationIds,
        teamIds,
      }
    },
    []
  )

  const finalLabel = label ?? i18n.t('common.employees')

  return (
    <EntitySelectorV2
      document={BookableEmployeeSelectorDocument}
      itemsDocument={BookableEmployeeSelectorItemsDocument}
      extractItems={extractItems}
      extractConnection={extractConnection}
      mapItemTitle={mapItemTitle}
      mapItemAvatar={mapItemAvatar}
      mapItemValue={mapItemValue}
      mapFiltersToVariables={mapFiltersToVariables}
      filters={filters}
      disabled={disabled}
      label={finalLabel}
      mandatory
      value={value}
      onChange={onChange}
    />
  )
}

export default BookableEmployeeSelector
